import { useEffect, useMemo, useReducer, useRef } from 'react';

import { verifyInvitation } from '../api';
import {
    initialInvitationState,
    invitationReducer,
    InvitationEvent,
    InvitationState,
} from '../machine/invitationMachine';

type InvitationFetcherResult = {
    next_step?: string | null;
    email?: string | null;
    message?: string | null;
};

type InvitationFetcher = (
    verifyToken: string
) => Promise<InvitationFetcherResult | undefined | null>;

export const useInvitationMachine = (
    verifyToken?: string,
    fetchInvitation: InvitationFetcher = verifyInvitation
) => {
    const [state, dispatch] = useReducer(invitationReducer, initialInvitationState);
    const lastVerifiedTokenRef = useRef<string | undefined>(undefined);

    const send = useMemo(() => (event: InvitationEvent) => dispatch(event), []);

    useEffect(() => {
        if (!verifyToken) return;
        if (lastVerifiedTokenRef.current === verifyToken) return;
        lastVerifiedTokenRef.current = verifyToken;
        dispatch({ type: 'LOAD' });
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchInvitation(verifyToken);
                if (cancelled) return;
                if (data && typeof data === 'object' && typeof data.next_step === 'string') {
                    dispatch({
                        type: 'RESOLVE',
                        payload: {
                            next_step: data.next_step,
                            email: data.email ?? undefined,
                            message: data.message ?? null,
                        },
                    });
                } else {
                    dispatch({ type: 'FAIL' });
                }
            } catch (e) {
                if (cancelled) return;
                dispatch({ type: 'FAIL', error: e });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [verifyToken, fetchInvitation]);

    return { state, send };
};
